import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {IDENTITY_SERVICE_INSTANCE} from '@/services/identity.service';
import {USER_SERVICE_INSTANCE} from '@/services/user.service';
import store, {SnackColorType} from '@/store';
import {DisguiseBusinessExceptionWrapper} from '@/exception/disguise-business-exception-wrapper';
import {LOGIN_ROUTE, ROUTER_SERVICE} from '@/services/router.service';

/**
 * Custom HTTP Service for getting pre-configured HTTP Client
 */
class HttpService {

    private static readonly DEFAULT_AXIOS_CONFIG: AxiosRequestConfig = {
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json;charset=utf-8'
        },
        validateStatus: ((status) => {
            // `validateStatus` defines whether to resolve or reject the promise for a given
            // HTTP response status code. If `validateStatus` returns `true` (or is set to `null`
            // or `undefined`), the promise will be resolved; otherwise, the promise will be rejected
            return status >= 200 && status < 400;
        })
    };

    private static REFRESH_SESSION_REQUEST_INTERCEPTOR: {
        onFulfilled?(value: AxiosRequestConfig): any | Promise<any>,
        onRejected?(error: any): any
    } = {
        onFulfilled(config: AxiosRequestConfig): any {
            if (IDENTITY_SERVICE_INSTANCE.isTokenExpired()
              && !IDENTITY_SERVICE_INSTANCE.isRefreshTokenExpired()) {
                return new Promise((resolve, reject) => {
                    return IDENTITY_SERVICE_INSTANCE.refreshToken().then(() => {
                        resolve(config);
                    }, (error) => {
                        reject(error);
                    });
                });
            } else {
                return config;
            }
        }
    };

    private static REFRESH_USER_DETAILS_RESPONSE_INTERCEPTOR: {
        onFulfilled?(value: AxiosResponse): any | Promise<any>,
        onRejected?(error: any): any
    } = {
        onFulfilled(response: AxiosResponse): any {
            const userProfileWasUpdated = (response.config.url || '')
                .startsWith(USER_SERVICE_INSTANCE.getFullApiPath())
              && response.config.method === 'put';
            if (userProfileWasUpdated) {
                // update user details after change
                USER_SERVICE_INSTANCE.loadUserDetails().then((userInfo) => {
                    // set the user info to the vuex store
                    store.commit('setUserInfo', {userInfo: userInfo.data});
                });
            }

            // Do something with response data
            return response;
        }
    };

    private static FORWARD_TO_LOGIN_PAGE_RESPONSE_INTERCEPTOR: {
        onFulfilled?(value: AxiosResponse): any | Promise<any>,
        onRejected?(error: any): any
    } = {
        onRejected: (error) => {
            const router = ROUTER_SERVICE.getInstance();
            if (error.response && error.response.status === 401 &&
              (!(router.currentRoute && router.currentRoute.name === LOGIN_ROUTE.name))) {
                // authorization failed. try to open login page with ?redirectTo=<originalPath>
                const queries: { redirectTo?: string } = {};

                const originalPath = window.location.pathname + window.location.search + window.location.hash;
                if (originalPath) {
                    // there is a target route. rescue the route via query param so that
                    // it can finally be restored.
                    queries.redirectTo = originalPath;
                }
                router.push({path: '/login', query: queries});
                // hacky solution, but there is no other way to break the call chain.
                // instead of forwarding the error to the next catch block, we return an never resolving promise.
                return new Promise(() => {
                    // never ending promise to break the call chain.
                });
            }
            return Promise.reject(error);
        }
    };

    private static DISGUISE_BUSINESS_EXCEPTION_RESPONSE_INTERCEPTOR: {
        onFulfilled?(value: AxiosResponse): any | Promise<any>,
        onRejected?(error: any): any
    } = {
        onRejected: (error) => {
            if (error.response && error.response.data && error.response.data['@type'] === 'DisguiseBusinessExceptionDto') {
                const castedResponse = error.response.data as DisguiseDto.DisguiseBusinessExceptionDto;
                let color: SnackColorType;
                switch (castedResponse.serenity) {
                    case 'WARN':
                        color = 'warning';
                        break;
                    case 'ERROR':
                        color = 'error';
                        break;
                    case 'INFO':
                        color = 'info';
                        break;
                    default:
                        color = 'warning';
                        break;
                }
                store.commit('showSnack', {
                    message: castedResponse.message,
                    color
                });
                return Promise.reject(
                  new DisguiseBusinessExceptionWrapper(castedResponse)
                );
            }
            return Promise.reject(error);
        }
    };

    private unAuthorizedAxios: AxiosInstance | null = null;
    private authorizedAxios: AxiosInstance | null = null;
    private authorizedAxiosWithoutLoginRedirect: AxiosInstance | null = null;

    /**
     * returns pre-configured HTTP Client without authentication
     */
    getUnauthorizedAxios() {
        if (!this.unAuthorizedAxios) {
            this.unAuthorizedAxios = axios.create(HttpService.DEFAULT_AXIOS_CONFIG);
        }
        return this.unAuthorizedAxios;
    }

    /**
     * returns pre-configured HTTP Client with authentication
     */
    getAxios() {
        if (!this.authorizedAxios) {
            const newAxiosInstance = axios.create(HttpService.DEFAULT_AXIOS_CONFIG);
            newAxiosInstance.interceptors.request.use(
              HttpService.REFRESH_SESSION_REQUEST_INTERCEPTOR.onFulfilled,
              HttpService.REFRESH_SESSION_REQUEST_INTERCEPTOR.onRejected
            );

            newAxiosInstance.interceptors.response.use(
              HttpService.REFRESH_USER_DETAILS_RESPONSE_INTERCEPTOR.onFulfilled,
              HttpService.REFRESH_USER_DETAILS_RESPONSE_INTERCEPTOR.onRejected
            );
            newAxiosInstance.interceptors.response.use(
              HttpService.FORWARD_TO_LOGIN_PAGE_RESPONSE_INTERCEPTOR.onFulfilled,
              HttpService.FORWARD_TO_LOGIN_PAGE_RESPONSE_INTERCEPTOR.onRejected
            );
            newAxiosInstance.interceptors.response.use(
              HttpService.DISGUISE_BUSINESS_EXCEPTION_RESPONSE_INTERCEPTOR.onFulfilled,
              HttpService.DISGUISE_BUSINESS_EXCEPTION_RESPONSE_INTERCEPTOR.onRejected
            );

            this.authorizedAxios = newAxiosInstance;
        }
        return this.authorizedAxios;
    }

    /**
     * returns pre-configured HTTP Client with authentication
     */
    getAxiosWithoutLoginRedirect() {
        if (!this.authorizedAxiosWithoutLoginRedirect) {
            const newAxiosInstance = axios.create(HttpService.DEFAULT_AXIOS_CONFIG);
            newAxiosInstance.interceptors.request.use(
              HttpService.REFRESH_SESSION_REQUEST_INTERCEPTOR.onFulfilled,
              HttpService.REFRESH_SESSION_REQUEST_INTERCEPTOR.onRejected
            );
            newAxiosInstance.interceptors.response.use(
              HttpService.REFRESH_USER_DETAILS_RESPONSE_INTERCEPTOR.onFulfilled,
              HttpService.REFRESH_USER_DETAILS_RESPONSE_INTERCEPTOR.onRejected
            );
            newAxiosInstance.interceptors.response.use(
              HttpService.DISGUISE_BUSINESS_EXCEPTION_RESPONSE_INTERCEPTOR.onFulfilled,
              HttpService.DISGUISE_BUSINESS_EXCEPTION_RESPONSE_INTERCEPTOR.onRejected
            );

            this.authorizedAxiosWithoutLoginRedirect = newAxiosInstance;
        }
        return this.authorizedAxiosWithoutLoginRedirect;
    }
}

const HTTP_SERVICE_INSTANCE = new HttpService();

export {HTTP_SERVICE_INSTANCE};
